import { html, type Html, Page, View } from 'rune-ts';
import klass from './CreatorShopFanLetter.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { getSharedData } from '../../../shared/util/rune';
import { footer_links } from '../../components/cells/Footer/constant';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MembershipTypeS } from '../../../features/Membership/outbound/share';
import { FanLetterListView } from '../../components/cells/FanLetterList/FanLetterListView';
import { FanLetterFormController } from '../../components/cells/FanLetterList/controller/FanLetterFormController';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import querystring from 'querystring';
import { FanLetterData } from '../../../features/FanLetter/type';
import { CreatorConstantS, CreatorTypeS } from '../../../features/Creator/outbound/share';
import { type LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { SubHeaderMobile } from '@services/MarppleShop/renderApp/components/cells/Header/Global/views/SubHeaderMobile.ts';
import { FanLetterItemController } from '../../components/cells/FanLetterItem/controller/FanLetterItemController';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { OnboardingApiS } from '@services/MarppleShop/marppleShopStudioApp/features/Onboarding/outbound/share.ts';
import { EventLogConstantS } from '@services/MarppleShop/features/EventLog/outbound/share.ts';
import { identity } from '@fxts/core';
import { CreatorShopMainBanner24View } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24View.ts';
import { LnbCreatorShop24View } from '@services/MarppleShop/renderApp/components/cells/Header/LnbCreatorShop24View.ts';
import { TCreatorShopLnbMenu } from '@services/MarppleShop/shared/constant';
import { BestCreatorRanking } from '@services/MarppleShop/features/CreatorRank/type/creator-rank.type.ts';
import { CreatorShopMainBanner24MoView } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24MoView.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';

export type CreatorShopFanLetterPageData = {
  layout_options: BaseLayoutOptions;
  creator: CreatorTypeS.CreatorMainProfile;
  is_store_owner: boolean;
  membership?: LocalsStoreMembership & {
    my: {
      token?: string;
      memberships?: MembershipTypeS.MembershipSummary[];
    };
  };
  adult_check_needed: boolean;
  fan_letters: FanLetterData[];
  is_login: boolean;
  page: number;
  total_count: number;
  menus: TCreatorShopLnbMenu[];
  best_store_ranking: BestCreatorRanking | undefined;
  store: {
    id: number;
  };
};

class CreatorShopFanLetterBody extends View<CreatorShopFanLetterPageData> {
  private fanLetterListView = new FanLetterListView({
    ...this.data,
    is_mobile: this.data.layout_options.is_mobile,
    list_type: !this.data.layout_options.is_mobile ? 'masonry' : 'vertical',
    item_mode: 'XLarge',
    grid_columns: 5,
  });

  private paginationView = new Pagination({
    page: this.data.page || 1,
    size: this.data.layout_options.is_mobile ? 5 : 10,
    total: Pagination.calcTotal({
      total_count: this.data.total_count,
      limit_per_page: CreatorShopFanLetterPage.LIMIT,
    }),
  });

  constructor(data: CreatorShopFanLetterPageData) {
    super(data);
    new FanLetterFormController(this.fanLetterListView.getFanLetterFormView());
    new FanLetterItemController(this.fanLetterListView.getFanLetterItemViews());
  }

  protected override template(data: CreatorShopFanLetterPageData): Html {
    const {
      layout_options: { is_mobile, user },
      creator,
      adult_check_needed,
      is_login,
      membership,
      menus,
      best_store_ranking,
      store,
    } = data;
    const sharedData = getSharedData(this);

    const MainBannerView = is_mobile ? CreatorShopMainBanner24MoView : CreatorShopMainBanner24View;

    const LnbView = is_mobile ? CreatorShopSubHeader : LnbCreatorShop24View;

    return html`
      <div class="${klass.page}">
        ${new MainBannerView({
          cover:
            (creator.is_adult && adult_check_needed) ||
            (is_mobile
              ? creator.banner_img_mo === CreatorConstantS.banner_img
              : creator.banner_img === CreatorConstantS.banner_img)
              ? 'No Cover'
              : 'Default',
          show_adult_creator_thumbnail: Boolean(creator.is_adult && adult_check_needed),
          is_owner: creator.user_id === user?.id,
          creator,
          is_login,
          membership,
          best_store_ranking,
          store,
        })}
        <div class="${klass.lnb_wrapper}">
          ${new LnbView({
            menus,
            plus_domain: sharedData?.plus_crew?.domain_name,
            focus_on_select: true,
          })}
        </div>

        <div class="${klass.body}">
          <div class="${klass.main}">
            <div class="${klass.fan_letters}">${this.fanLetterListView}</div>
            ${this.paginationView.isHidden
              ? ''
              : html`<div class="${klass.pagination}">${this.paginationView}</div>`}
          </div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    SubHeaderMobile.handleChangeMenuOutside();
    this.delegate(PaginationChangeEvent, Pagination, async (e) => {
      const { page } = e.detail;
      const query = querystring.parse(location.search.slice(1));

      location.href = location.pathname + '?' + querystring.stringify({ ...query, page });
    });
  }
}

export class CreatorShopFanLetterPage extends Page<CreatorShopFanLetterPageData> {
  public static LIMIT = 100;
  protected override template(): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = this.data;

    return html` <div>
      ${new LayoutView(
        {
          body: new CreatorShopFanLetterBody(this.data),
          footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
        },
        {
          ...layout_options,
          full_width: true,
          has_bottom_tab_bar: !sharedData?.webviewapp,
        },
      )}
    </div>`;
  }

  override onRender() {
    /**
     * @desc 마플샵 크리에이터 온보딩 리뷰 관련 완료
     * @see EventLogConstantS.ACTION_TYPE.CREATOR_STUDIO_ONBOARDING.FIRST_CHAT_CHEER
     */
    if (UtilS.isMarppleShopApp() && box.sel('store_user_id') === box.sel('is_user->id') && ET.lang === 'kr') {
      OnboardingApiS.logCreatorStudioOnboardingAction(
        { store_id: box.sel('store_id') },
        {
          action_type: EventLogConstantS.ACTION_TYPE.CREATOR_STUDIO_ONBOARDING.FIRST_CHAT_CHEER,
          user_id: box.sel('store_user_id'),
        },
      ).catch(identity);
    }
  }
}
